import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  Dialog,
  Badge,
  Tabs,
  Slider,
  TextField,
  Checkbox,
  Grid,
} from '@radix-ui/themes';
import { useEffect, useState, useRef, useCallback } from 'react';
import RecordRTC from 'recordrtc';
import { useOrganisation } from '~/hooks/useOrganisation';
import {
  listAutomationRuns,
  runAutomation,
  getAutomation,
  getAutomationRun,
  createAutomationRecording,
  updateAutomation,
} from '~/services/versori-api/automations';
import {
  TbRobot,
  TbRefresh,
  TbChevronRight,
  TbMicrophone,
  TbEdit,
  TbKey,
  TbTemplate,
  TbVideo,
  TbList,
} from 'react-icons/tb';
import { Header } from '~/features/automations/components/Header';
import { DocumentTitle } from '~/components/DocumentTitle';
import type { Automation, AutomationSecret } from '~/services/versori-api/types';
import { AccountDropdown } from '~/components/AccountDropdown/AccountDropdown';
import { useSession } from '~/features/auth/context/Session';
import { RecordingsList } from '~/components/RecordingsList';
import { PromptWithVariables } from '~/components/PromptWithVariables';
import { PromptDisplay } from '~/components/PromptDisplay';
import { SecretsSection } from '~/features/automations/components/SecretsSection';
import { AutomationRunsTable } from '~/features/automations/components/AutomationRunsTable';
import { DelayedRenderingComponent } from '~/components/DelayedRenderingComponent';
import { LoadingSpinner } from '~/components/LoadingSpinner/LoadingSpinner';
import Styles from '~/features/automations/css/automations.module.scss';

export const Route = createFileRoute('/_auth/automations/$automationId/')({
  component: AutomationDetailsScaffold,
});

interface RunDetails {
  id: string;
  status: string;
  prompt?: string;
  executionId?: string;
  createdAt?: string;
  updatedAt?: string;
  result?: any;
  artifacts?: {
    name: string;
    url: string;
  }[];
}

// Define extended MediaStream type with added properties for cleanup
interface ExtendedMediaStream extends MediaStream {
  screenStream?: MediaStream;
  micStream?: MediaStream;
}

// Custom hook for screen recording
function useScreenRecording(
  organisationId: string | undefined,
  automationId: string,
  setError: (error: string | null) => void,
  onRecordingComplete?: () => void
) {
  const [isRecording, setIsRecording] = useState(false);
  const [addVoiceover, setAddVoiceover] = useState(false);
  const recordRTCRef = useRef<RecordRTC | null>(null);
  const streamRef = useRef<ExtendedMediaStream | null>(null);

  const toggleRecording = useCallback(async () => {
    // Start recording
    if (!isRecording) {
      try {
        const displayMediaOptions = {
          video: {
            cursor: 'always',
          } as any,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
          } as any,
        } as DisplayMediaStreamOptions;

        const screenStream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);

        // Store reference to screen stream for cleanup
        const combinedStream = new MediaStream() as ExtendedMediaStream;
        combinedStream.screenStream = screenStream;

        // Add all screen tracks to the combined stream
        screenStream.getTracks().forEach((track) => {
          combinedStream.addTrack(track);

          // Add listener for when user stops sharing via the browser UI
          track.addEventListener('ended', () => {
            if (isRecording && recordRTCRef.current) {
              // Auto-stop recording if screen sharing ends
              recordRTCRef.current.stopRecording();

              // Clean up all streams
              if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());

                if (streamRef.current.micStream) {
                  streamRef.current.micStream.getTracks().forEach((t) => t.stop());
                }

                if (streamRef.current.screenStream) {
                  streamRef.current.screenStream.getTracks().forEach((t) => t.stop());
                }
              }

              // Reset recording state
              setIsRecording(false);
              recordRTCRef.current = null;
              streamRef.current = null;
            }
          });
        });

        // Only get microphone stream if voiceover is enabled
        if (addVoiceover) {
          try {
            const micStream = await navigator.mediaDevices.getUserMedia({
              audio: {
                echoCancellation: true,
                noiseSuppression: true,
              },
              video: false,
            });

            // Store reference to mic stream for cleanup
            combinedStream.micStream = micStream;

            // Add all microphone tracks to the combined stream
            micStream.getAudioTracks().forEach((track) => {
              combinedStream.addTrack(track);
            });
          } catch (micError) {
            setError('Microphone access denied. Recording will continue without voice.');
          }
        }

        // Setup RecordRTC with the combined stream
        const recorder = new RecordRTC(combinedStream, {
          type: 'video',
          mimeType: 'video/webm',
          recorderType: RecordRTC.MediaStreamRecorder,
          disableLogs: false,
          timeSlice: 1000,
          bitsPerSecond: 128000,
        });

        // Start recording
        recorder.startRecording();

        recordRTCRef.current = recorder;
        streamRef.current = combinedStream;

        setIsRecording(true);
      } catch (err) {
        setError('Failed to start recording. Please check permissions.');
      }
    }
    // Stop recording
    else {
      if (recordRTCRef.current && streamRef.current) {
        recordRTCRef.current.stopRecording(async () => {
          const blob = recordRTCRef.current?.getBlob();

          if (blob && organisationId) {
            const timestamp = new Date().toISOString();
            const filename = `recording-${timestamp}.webm`;

            // Upload the recording
            try {
              const recordingResponse = await createAutomationRecording(organisationId, automationId, {
                fileName: filename,
                contentType: 'video/webm',
                expirySeconds: 300,
                hasVoiceover: addVoiceover,
              });

              const file = new File([blob], filename, { type: 'video/webm' });

              try {
                const response = await fetch(recordingResponse.url, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'video/webm',
                  },
                  body: file,
                });

                if (!response.ok) {
                  throw new Error(`Failed to upload: ${response.status} ${response.statusText}`);
                }

                // Call the callback if provided
                if (onRecordingComplete) {
                  onRecordingComplete();
                }
              } catch (error) {
                setError('Failed to upload recording');
              }
            } catch (err) {
              setError('Failed to create recording');
            }
          }

          // Stop all tracks in all streams
          streamRef.current?.getTracks().forEach((track) => track.stop());

          // Also stop tracks in the original streams if they exist
          if (streamRef.current?.screenStream) {
            streamRef.current.screenStream.getTracks().forEach((track) => track.stop());
          }

          if (streamRef.current?.micStream) {
            streamRef.current.micStream.getTracks().forEach((track) => track.stop());
          }

          // Reset references
          streamRef.current = null;
          recordRTCRef.current = null;
          setIsRecording(false);
        });
      }
    }
  }, [isRecording, addVoiceover, organisationId, automationId, setError, onRecordingComplete]);

  const toggleVoiceover = useCallback(() => {
    setAddVoiceover((prev) => !prev);
  }, []);

  return {
    isRecording,
    addVoiceover,
    toggleRecording,
    toggleVoiceover,
  };
}

function AutomationDetailsScaffold() {
  const { automationId } = Route.useParams() as { automationId: string };
  const navigate = useNavigate();
  const { session, handleLogin } = useSession();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { organisation } = useOrganisation();
  const [runs, setRuns] = useState<RunDetails[]>([]);
  const [automation, setAutomation] = useState<Automation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [taskDescription, setTaskDescription] = useState('Run this automation');
  const [processedTaskDescription, setProcessedTaskDescription] = useState('');
  const [activeTab, setActiveTab] = useState<string>('template');

  // Add new state for edit template dialog
  const [showEditTemplateDialog, setShowEditTemplateDialog] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState('');
  const [processedTemplate, setProcessedTemplate] = useState('');
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);
  const [updateTemplateError, setUpdateTemplateError] = useState<string | null>(null);

  // Add new state for credentials
  const [isUpdatingCredentials, setIsUpdatingCredentials] = useState(false);
  const [updateCredentialsError, setUpdateCredentialsError] = useState<string | null>(null);

  // Add state for maxSteps configuration
  const [maxSteps, setMaxSteps] = useState<number>(30);

  // Add state for webhook URL configuration
  const [webhookUrl, setWebhookUrl] = useState<string>('');
  const [isWebhookUrlVisible, setIsWebhookUrlVisible] = useState(false);

  // Fetch runs
  const fetchRuns = async () => {
    if (!organisation?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const runsData = await listAutomationRuns(organisation.id, automationId);
      // Cast the result to the expected type
      setRuns(runsData as unknown as RunDetails[]);

      // Set the active tab based on whether there are runs
      if (runsData && runsData.length > 0) {
        setActiveTab('runs');
      } else {
        setActiveTab('recordings');
      }
    } catch (err) {
      console.error('Error fetching runs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch runs');
      // Default to recordings tab if there's an error
      setActiveTab('recordings');
    } finally {
      setIsLoading(false);
    }
  };

  // Use the custom recording hook
  const { isRecording, addVoiceover, toggleRecording, toggleVoiceover } = useScreenRecording(
    organisation?.id,
    automationId,
    setError,
    fetchRuns // Pass the fetchRuns function as the onRecordingComplete callback
  );

  // Fetch automation details
  const fetchAutomation = async () => {
    if (!organisation?.id) return;

    try {
      const data = await getAutomation(organisation.id, automationId);
      setAutomation(data);
    } catch (err) {
      console.error('Failed to fetch automation details:', err);
    }
  };

  // Helper to get status badge color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running':
        return 'blue';
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  // Open the run dialog
  const handleOpenRunDialog = () => {
    // Use the prompt template as the default if available
    setTaskDescription(automation?.promptTemplate || `Run ${automation?.name || ''}`);
    setShowRunDialog(true);
  };

  // Run the automation with the provided task description
  const handleRunAutomation = async () => {
    if (!organisation?.id) return;
    setIsRunning(true);
    setError(null);
    setShowRunDialog(false);

    try {
      // Use processed task description if available, otherwise use the regular one
      const finalPrompt = processedTaskDescription || taskDescription;

      // Only pass webhook URL if checkbox is checked and URL is not empty
      const finalWebhookUrl = isWebhookUrlVisible && webhookUrl.trim() ? webhookUrl : undefined;

      const result = await runAutomation(organisation.id, automationId, finalPrompt, maxSteps, finalWebhookUrl);
      if (result && result.id) {
        navigate({ to: '/automations/$automationId/runs/$runId', params: { automationId, runId: result.id } });
      }
      // Refresh the runs list
      await fetchRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run automation');
    } finally {
      setIsRunning(false);
    }
  };

  // Open the edit template dialog
  const handleOpenEditTemplateDialog = () => {
    setEditedTemplate(automation?.promptTemplate || '');
    setProcessedTemplate('');
    setShowEditTemplateDialog(true);
  };

  // Save the edited template
  const handleSaveTemplate = async () => {
    if (!organisation?.id) return;

    setIsUpdatingTemplate(true);
    setUpdateTemplateError(null);

    try {
      // Always use the edited template to preserve mustache syntax {{variable}}
      const finalTemplate = editedTemplate;

      const updatedAutomation = await updateAutomation(organisation.id, automationId, {
        promptTemplate: finalTemplate,
      });

      // Update the automation in state
      setAutomation(updatedAutomation);
      setShowEditTemplateDialog(false);
    } catch (err) {
      console.error('Failed to update template:', err);
      setUpdateTemplateError(err instanceof Error ? err.message : 'Failed to update automation template');
    } finally {
      setIsUpdatingTemplate(false);
    }
  };

  // Handle saving credentials
  const handleSaveCredentials = async (secrets: AutomationSecret[]) => {
    if (!organisation?.id) return;

    setIsUpdatingCredentials(true);
    setUpdateCredentialsError(null);

    try {
      // Update the credential with all provided secrets
      // This will replace any existing credential's secrets
      const updatedAutomation = await updateAutomation(organisation.id, automationId, {
        secrets: secrets,
      });

      // Update the automation in state
      setAutomation(updatedAutomation);
    } catch (err) {
      console.error('Failed to update automation secrets:', err);
      setUpdateCredentialsError(err instanceof Error ? err.message : 'Failed to update automation secrets');
    } finally {
      setIsUpdatingCredentials(false);
    }
  };

  useEffect(() => {
    fetchRuns();
    fetchAutomation();
  }, [organisation?.id, automationId]);

  const [allowPageToRender, setAllowPageToRender] = useState(false);

  return (
    <>
      <DocumentTitle title={automation?.name ? `${automation.name} | Versori` : 'Automation Details | Versori'} />
      <Grid
        height="100vh"
        width="100%"
        style={{
          display: 'grid',
          gridTemplateAreas: `
          "header"
          "interface"
        `,
          gridTemplateRows: '5.5rem 1fr',
        }}
      >
        <Header title={automation?.name} onSetShowRunDialog={setShowRunDialog} />
        <div className={Styles.AutomationsPage}>
          {/* {workspaceProject.id && allowPageToRender ? (
            <Workspace />
          ) : isError ? (
            <Flex justify="center" align="center" height="100%" width="100%">
              <SwitchOrganisation />
            </Flex>
          ) : (
            <Flex justify="center" align="center" height="100%" width="100%">
              <DelayedRenderingComponent delay={500} onAllowContinue={() => setAllowPageToRender(true)}>
                <LoadingSpinner width="6rem" height="6rem" />
              </DelayedRenderingComponent>
            </Flex>
          )} */}

          {automation && allowPageToRender ? (
            <>
              <Tabs.Root defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab}>
                <Tabs.List className="mb-4">
                  <Tabs.Trigger value="template">
                    <Flex align="center" gap="2">
                      <TbTemplate />
                      Template
                    </Flex>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="secrets">
                    <Flex align="center" gap="2">
                      <TbKey />
                      Secrets
                    </Flex>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="recordings">
                    <Flex align="center" gap="2">
                      <TbVideo />
                      Recordings
                    </Flex>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="runs">
                    <Flex align="center" gap="2">
                      <TbList />
                      Runs
                    </Flex>
                  </Tabs.Trigger>
                </Tabs.List>

                <Box p="3">
                  {/* Template Tab Content */}
                  <Tabs.Content value="template">
                    <Card className="p-8 mx-4">
                      <Heading size="4" mb="6">
                        Testing Login
                      </Heading>
                      <Flex direction="column" gap="6">
                        <Box>
                          <Text size="2" color="gray">
                            ID: {automationId}
                          </Text>
                        </Box>
                        {automation.description && (
                          <Box>
                            <Text>{automation.description}</Text>
                          </Box>
                        )}

                        <Box>
                          <Flex justify="between" align="center" mb="2">
                            <Text size="2" color="gray" weight="medium">
                              Prompt Template:
                            </Text>
                            <Button variant="ghost" onClick={handleOpenEditTemplateDialog} aria-label="Edit template">
                              <TbEdit size={18} />
                              Edit
                            </Button>
                          </Flex>

                          {automation.promptTemplate ? (
                            <Box mt="1" className="p-4 bg-gray-50 rounded-md text-sm">
                              <pre className="whitespace-pre-wrap font-sans text-sm">{automation.promptTemplate}</pre>
                            </Box>
                          ) : (
                            <Box mt="1" className="p-3 bg-gray-50 rounded-md text-sm">
                              <Flex justify="between" align="center">
                                <Text color="gray">No prompt template defined</Text>
                              </Flex>
                            </Box>
                          )}
                        </Box>
                      </Flex>
                    </Card>
                  </Tabs.Content>

                  {/* Secrets Tab Content */}
                  <Tabs.Content value="secrets">
                    <Card className="p-8 mx-4">
                      <SecretsSection
                        credentialNames={automation.credentialNames}
                        onSaveCredentials={handleSaveCredentials}
                        isLoading={isUpdatingCredentials}
                        error={updateCredentialsError}
                      />
                    </Card>
                  </Tabs.Content>

                  {/* Recordings Tab Content */}
                  <Tabs.Content value="recordings">
                    <Card className="p-8 mx-4">
                      <Heading size="5" mb="2">
                        Recordings
                      </Heading>
                      <Flex justify="between" align="center" gap="2" mb="3">
                        <Box>
                          <Text color="gray">
                            Record your screen and audio to automatically generate new prompts for this automation.
                          </Text>
                        </Box>
                        <Flex gap="2">
                          <Flex align="center" gap="2">
                            <Text size="2">Add voiceover</Text>
                            <Button
                              variant="soft"
                              color={addVoiceover ? 'green' : 'gray'}
                              onClick={toggleVoiceover}
                              disabled={isRecording || isLoading || isRunning}
                            >
                              {addVoiceover ? 'Yes' : 'No'}
                            </Button>
                          </Flex>
                          <Button
                            variant="soft"
                            color={isRecording ? 'red' : 'green'}
                            onClick={toggleRecording}
                            disabled={isLoading || isRunning}
                          >
                            <Flex align="center" gap="2">
                              <TbMicrophone />
                              <span>{isRecording ? 'Stop Recording' : 'Record'}</span>
                            </Flex>
                          </Button>
                        </Flex>
                      </Flex>

                      {isRecording && (
                        <Card className="mb-4 p-3 bg-red-50 border-red-200">
                          <Flex align="center" gap="3">
                            <Box className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></Box>
                            <Text weight="medium" color="red">
                              Recording in progress...
                            </Text>
                            <Text size="2" color="gray">
                              {addVoiceover ? 'Screen and voice being recorded' : 'Screen being recorded'}
                            </Text>
                          </Flex>
                        </Card>
                      )}

                      {/* Add the RecordingsList component */}
                      {automation && <RecordingsList automationId={automationId} />}
                    </Card>
                  </Tabs.Content>

                  {/* Runs Tab Content */}
                  <Tabs.Content value="runs">
                    {organisation?.id && (
                      <AutomationRunsTable
                        organisationId={organisation.id}
                        automationId={automationId}
                        automationName={automation.name}
                      />
                    )}
                  </Tabs.Content>
                </Box>
              </Tabs.Root>
            </>
          ) : (
            <Flex justify="center" align="center" height="100%" width="100%">
              <DelayedRenderingComponent delay={500} onAllowContinue={() => setAllowPageToRender(true)}>
                <LoadingSpinner width="6rem" height="6rem" />
              </DelayedRenderingComponent>
            </Flex>
          )}
        </div>
        {/* Keep all existing dialogs */}

        {/* Run Dialog */}
        <Dialog.Root open={showRunDialog} onOpenChange={setShowRunDialog}>
          <Dialog.Content style={{ maxWidth: '650px' }}>
            <Dialog.Title>Run Automation</Dialog.Title>
            <Dialog.Description size="2" mb="2">
              Provide a prompt to run this automation.
            </Dialog.Description>

            {/* Moved the secrets section outside of Dialog.Description to avoid nesting divs in p tags */}
            {automation?.credentialNames && automation.credentialNames.length > 0 && (
              <Box mb="4">
                <Text size="2" color="gray">
                  You can use the following automation secrets in your prompt with the syntax:
                  {' {{secret:name}}'}
                </Text>
                <Flex gap="2" wrap="wrap" mt="1">
                  {automation.credentialNames.map((name, i) => (
                    <Badge key={i} color="blue" variant="soft">
                      <TbKey size={12} className="mr-1" />
                      {name}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )}

            <Flex direction="column" gap="3">
              <PromptWithVariables
                initialPrompt={taskDescription}
                onPromptChange={setTaskDescription}
                onProcessedPromptChange={setProcessedTaskDescription}
                label="Task Description"
                placeholder="Describe what you want the automation to do..."
                buttonText="Run"
                availableSecrets={automation?.credentialNames || []}
              />

              {/* Max Steps Configuration */}
              <Box>
                <Flex align="center" gap="3" mb="2">
                  <Text size="2" color="gray" weight="medium">
                    Maximum Steps:
                  </Text>
                  <Text size="2" weight="bold">
                    {maxSteps}
                  </Text>
                </Flex>
                <Slider
                  value={[maxSteps]}
                  min={10}
                  max={100}
                  step={1}
                  onValueChange={(value: number[]) => {
                    setMaxSteps(value[0]);
                  }}
                  size="2"
                />
                <Flex justify="between" mt="1">
                  <Text size="1" color="gray">
                    10
                  </Text>
                  <Text size="1" color="gray">
                    100
                  </Text>
                </Flex>
                <Text size="1" color="gray" mt="1">
                  The maximum number of steps the automation agent can take (default: 30)
                </Text>
              </Box>

              {/* Webhook URL Configuration */}
              <Box>
                <Flex align="center" gap="3" mb="2">
                  <Checkbox
                    checked={isWebhookUrlVisible}
                    onCheckedChange={(checked) => setIsWebhookUrlVisible(checked as boolean)}
                  />
                  <Text size="2" weight="medium">
                    Versori Integration Workflow Webhook URL
                  </Text>
                </Flex>
                {isWebhookUrlVisible && (
                  <Flex direction="column" gap="2" mt="1">
                    <TextField.Root
                      style={{ width: '100%' }}
                      placeholder="Enter a webhook URL (optional)"
                      value={webhookUrl}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWebhookUrl(e.target.value)}
                    />
                    <Text size="1" color="gray">
                      If provided, the automation will send a POST request to this URL with the execution result.
                    </Text>
                  </Flex>
                )}
              </Box>
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button onClick={handleRunAutomation}>Run Automation</Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        {/* Edit Template Dialog */}
        <Dialog.Root open={showEditTemplateDialog} onOpenChange={setShowEditTemplateDialog}>
          <Dialog.Content style={{ maxWidth: '900px', width: '90%' }}>
            <Dialog.Title>Edit Prompt Template</Dialog.Title>
            <Dialog.Description size="2" mb="2">
              Edit the automation's prompt template.
            </Dialog.Description>

            {/* Moved the secrets section outside of Dialog.Description to avoid nesting divs in p tags */}
            {automation?.credentialNames && automation.credentialNames.length > 0 && (
              <Box mb="4">
                <Text size="2" color="gray">
                  You can use the following automation secrets in your prompt with the syntax:
                  {' {{secret:name}}'}
                </Text>
                <Flex gap="2" wrap="wrap" mt="1">
                  {automation.credentialNames.map((name, i) => (
                    <Badge key={i} color="blue" variant="soft">
                      <TbKey size={12} className="mr-1" />
                      {name}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )}

            <PromptWithVariables
              initialPrompt={editedTemplate}
              onPromptChange={setEditedTemplate}
              onProcessedPromptChange={setProcessedTemplate}
              label="Prompt Template"
              placeholder="Enter or edit the prompt template..."
              inputHeight="300px"
              buttonText="Save"
            />

            {updateTemplateError && (
              <Card className="mt-4 p-3 bg-red-50 text-red-700">
                <Text>{updateTemplateError}</Text>
              </Card>
            )}

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button onClick={handleSaveTemplate} disabled={!editedTemplate.trim() || isUpdatingTemplate}>
                {isUpdatingTemplate ? 'Saving...' : 'Save'}
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </Grid>
    </>
  );
}
